import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/api/client";
import type { DocumentItem, DocumentUpdate } from "@/api/types";
import { tokenStorage } from "@/auth/tokenStorage";
import { env } from "@/lib/env";
import { uploadDocumentXhr } from "@/lib/upload";

const KEY = (kbId: string) => ["kb-documents", kbId] as const;
const ROOT_KEY = ["knowledge-bases"] as const;

export function useKbDocuments(kbId: string) {
  return useQuery({
    queryKey: KEY(kbId),
    queryFn: () =>
      api.GET<DocumentItem[]>(`/knowledge-bases/${kbId}/documents`),
    enabled: Boolean(kbId),
    // Polling 5s enquanto houver pending/processing.
    refetchInterval: (q) => {
      const data = q.state.data as DocumentItem[] | undefined;
      if (!data) return 5000;
      const ongoing = data.some(
        (d) => d.status === "pending" || d.status === "processing",
      );
      return ongoing ? 5000 : false;
    },
  });
}

/**
 * Documentos de VÁRIAS KBs agregados (uma query por KB, via ``useQueries``;
 * compartilha o cache de ``useKbDocuments`` pela mesma queryKey). Usado por
 * seletores que precisam listar documentos das KBs associadas a um agente
 * (ex.: escolher o template/logo do relatório por NOME, não por ID colado).
 */
export function useKbsDocuments(kbIds: string[]) {
  const results = useQueries({
    queries: kbIds.map((kbId) => ({
      queryKey: KEY(kbId),
      queryFn: () =>
        api.GET<DocumentItem[]>(`/knowledge-bases/${kbId}/documents`),
      enabled: Boolean(kbId),
      staleTime: 30_000,
    })),
  });
  // Dedupe por id: blinda contra kbIds repetidos (cada query carrega o mesmo
  // array, e o flatMap emitiria o doc 2x -> chave/value duplicados no Select).
  const byId = new Map<string, DocumentItem>();
  for (const r of results) {
    for (const d of r.data ?? []) byId.set(d.id, d);
  }
  const documents = [...byId.values()];
  const isLoading = results.some((r) => r.isLoading);
  return { documents, isLoading };
}

export function useDeleteDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.DELETE(`/documents/${documentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY(kbId) });
      void qc.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}

export function useUpdateDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; patch: DocumentUpdate }) =>
      api.PATCH<DocumentItem>(`/documents/${input.documentId}`, input.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY(kbId) });
    },
  });
}

export function useReindexDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.POST<DocumentItem>(`/documents/${documentId}/reindex`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY(kbId) });
    },
  });
}

/**
 * Faz download do arquivo original de um documento via API com auth
 * header e dispara o save no navegador. O bucket e privado, entao NAO
 * usamos ``<a href>`` direto (mesmo padrao de downloadAttachment).
 */
export async function downloadDocument(
  documentId: string,
  filename: string,
): Promise<void> {
  const token = await tokenStorage.getToken();
  const resp = await fetch(
    `${env.API_BASE_URL}/documents/${documentId}/download`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!resp.ok) {
    throw new Error(`Download falhou: HTTP ${resp.status}`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function useUploadDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      file: File;
      onProgress?: (pct: number) => void;
      signal?: AbortSignal;
    }) =>
      uploadDocumentXhr(kbId, input.file, {
        onProgress: input.onProgress,
        signal: input.signal,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY(kbId) });
      void qc.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}
